import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, query, orderBy, setDoc, deleteDoc, where } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signOut, getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { db, firebaseConfig } from '../firebase/config';
import { Staff as StaffType } from '../types';
import { useToast } from '../contexts/ToastContext';
import StaffForm from '../components/forms/StaffForm';
import StaffView from '../components/StaffView';
import { useAuth } from '../contexts/AuthContext';
import useTenantLimits from '../hooks/useTenantLimits';
import { recalculateStaffSummary, onStaffSummary, StaffSummary } from '../services/summary.service';

const StaffList: React.FC = () => {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';
    const isAuthorized = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'hr' || user?.role === 'principal';
    const [staff, setStaff] = useState<StaffType[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingStaff, setEditingStaff] = useState<StaffType | null>(null);
    const { showSuccess, showError } = useToast();
    const { isLimitReached, getLimit } = useTenantLimits();
    const [summary, setSummary] = useState<StaffSummary>({ total: 0, active: 0, inactive: 0, resigned: 0 });

    useEffect(() => {
        const unsub = onStaffSummary(setSummary);
        return () => unsub();
    }, []);

    useEffect(() => {
        fetchStaff();
    }, []);

    const fetchStaff = async () => {
        try {
            const staffRef = collection(db, 'staff');
            const q = query(staffRef, orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            const staffData = querySnapshot.docs.map(doc => {
                const data = doc.data();
                console.log('Staff document ID:', doc.id, 'UID field:', data.uid);
                return {
                    id: doc.id,
                    ...data,
                    dateOfBirth: data.dateOfBirth?.toDate(),
                    createdAt: data.createdAt?.toDate(),
                    updatedAt: data.updatedAt?.toDate(),
                    currentPosition: {
                        ...data.currentPosition,
                        joiningDate: data.currentPosition?.joiningDate?.toDate(),
                    },
                };
            }) as StaffType[];
            setStaff(staffData);
        } catch (error) {
            console.error('Error fetching staff:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (staffMember: StaffType) => {
        if (!isAuthorized) {
            showError('Access Denied', 'You do not have permission to edit staff members.');
            return;
        }
        setEditingStaff(staffMember);
        setShowForm(true);
    };

    const handleFormClose = () => {
        setShowForm(false);
        setEditingStaff(null);
    };

    const handleFormSubmit = async (staffData: Omit<StaffType, 'id' | 'createdAt' | 'updatedAt'>) => {
        if (!isAuthorized) {
            showError('Access Denied', 'You do not have permission to create or update staff members.');
            return;
        }
        try {
            setLoading(true);

            if (editingStaff) {
                // Update existing staff
                const staffRef = doc(db, 'staff', editingStaff.id);
                const { password, confirmPassword, ...updateData } = staffData;
                await updateDoc(staffRef, {
                    ...updateData,
                    updatedAt: new Date()
                });
                showSuccess('Staff Updated', 'Staff member has been successfully updated.');
            } else {
                // First check if a teacher with this email already exists
                const teachersRef = collection(db, 'teachers');
                const teacherQuery = query(teachersRef, where('email', '==', staffData.email.trim().toLowerCase()));
                const teacherSnap = await getDocs(teacherQuery);

                let uid = '';
                let linked = false;

                if (!teacherSnap.empty) {
                    uid = teacherSnap.docs[0].id;
                    linked = true;
                    console.log('Found existing teacher with same email. Linking staff. UID:', uid);
                } else {
                    // Create new staff with Firebase Auth
                    if (!staffData.password) {
                        showError('Password Required', 'Password is required for new staff members.');
                        return;
                    }

                    // Create a separate Firebase app instance to avoid interfering with current user session
                    const tempApp = initializeApp(firebaseConfig, 'temp-staff-creation');
                    const tempAuth = getAuth(tempApp);

                    // Create Firebase Auth user using temporary auth instance
                    const userCredential = await createUserWithEmailAndPassword(
                        tempAuth,
                        staffData.email,
                        staffData.password
                    );
                    uid = userCredential.user.uid;

                    // Sign out from the temporary auth instance (doesn't affect main auth)
                    await signOut(tempAuth);
                }

                // Create staff document in Firestore using UID as document ID
                const { password, confirmPassword, ...staffDoc } = staffData;
                const staffRef = doc(db, 'staff', uid);
                console.log('Creating/setting staff document with ID:', uid);
                await setDoc(staffRef, {
                    ...staffDoc,
                    uid: uid, // Link to Firebase Auth user
                    createdAt: new Date(),
                    updatedAt: new Date()
                });
                console.log('Staff document created with ID:', staffRef.id);

                if (linked) {
                    showSuccess('Staff Linked', 'An existing teacher account with this email was found. This staff profile has been linked to it successfully.');
                } else {
                    showSuccess('Staff Created', 'Staff member has been successfully created! They can now login with their email and password.');
                }
            }

            fetchStaff(); // Refresh the list
            recalculateStaffSummary().catch(console.error);
            handleFormClose();
        } catch (error: any) {
            console.error('Error saving staff:', error);
            showError('Operation Failed', error.message || 'An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (staffId: string, newStatus: 'active' | 'inactive') => {
        if (!isAuthorized) {
            showError('Access Denied', 'You do not have permission to update staff status.');
            return;
        }
        try {
            setLoading(true);
            const staffRef = doc(db, 'staff', staffId);
            await updateDoc(staffRef, {
                'currentPosition.status': newStatus,
                updatedAt: new Date()
            });
            showSuccess('Status Updated', `Staff status has been updated to ${newStatus}.`);
            fetchStaff(); // Refresh the list
            recalculateStaffSummary().catch(console.error);
        } catch (error: any) {
            console.error('Error updating staff status:', error);
            showError('Update Failed', error.message || 'Failed to update staff status. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteStaff = async (staffId: string) => {
        if (!window.confirm('Are you sure you want to delete this staff member?')) return;
        try {
            setLoading(true);
            await deleteDoc(doc(db, 'staff', staffId));
            showSuccess('Staff Deleted', 'Staff member has been deleted.');
            fetchStaff();
            recalculateStaffSummary().catch(console.error);
        } catch (error: any) {
            console.error('Error deleting staff:', error);
            showError('Delete Failed', error.message || 'Failed to delete staff member.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddStaff = () => {
        if (!isAuthorized) {
            showError('Access Denied', 'You do not have permission to add staff members.');
            return;
        }
        if (!isSuperAdmin && isLimitReached('Staff', staff.length)) {
            const lim = getLimit('Staff');
            showError(
                'Staff Limit Reached',
                `You have reached the maximum limit of ${lim} staff members. Please contact Dynamix Zone to increase your Staff limit.`
            );
            return;
        }
        setShowForm(true);
    };

    return (
        <div className="space-y-5">
            <StaffView
                staff={staff}
                onEdit={handleEdit}
                onToggleStatus={handleToggleStatus}
                onDelete={handleDeleteStaff}
                onAddStaff={handleAddStaff}
                loading={loading}
                summary={summary}
            />

            {/* Staff Form Modal */}
            {showForm && (
                <StaffForm
                    staff={editingStaff || undefined}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormClose}
                    loading={loading}
                />
            )}
        </div>
    );
};

const StaffDetail: React.FC = () => {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Staff Details</h2>
                <button
                    onClick={() => window.history.back()}
                    className="btn-secondary"
                >
                    Back
                </button>
            </div>
            <div className="card">
                <p className="text-gray-600">Staff detail view will be implemented here.</p>
            </div>
        </div>
    );
};

const Staff: React.FC = () => {
    return (
        <Routes>
            <Route path="/" element={<StaffList />} />
            <Route path="/new" element={<StaffList />} />
            <Route path="/:id" element={<StaffDetail />} />
        </Routes>
    );
};

export default Staff;
